import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  CircularProgress,
  Box,
  Autocomplete,
  Typography,
  InputAdornment,
  IconButton,
  Tooltip,
  Chip,
  Skeleton
} from
  '@mui/material';
import { AutoAwesome as AutoGenerateIcon, LocationOn, Devices } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { devicesApi, locationsApi } from '../../services/apiClient';
import locationService from '../../services/locationService';
import TreeSelect from '../TreeSelect';
import LocationTreeView from '../LocationTreeView';
import ResponsiveDialog from '../ResponsiveDialog'; import { useThemeSettings } from "../../contexts/ThemeContext";


const DeviceDialog = ({
  open,
  mode = 'create', // 'create' or 'edit'
  device = null,
  parentDevice = null,
  onClose,
  onSave
}) => {
  const { t } = useThemeSettings();
  const { enqueueSnackbar } = useSnackbar();
  const [formData, setFormData] = useState({
    friendlyName: '',
    deviceTypeId: '',
    locationId: '',
    parentDeviceId: '',
    lanIP: '',
    wanIP: '',
    macAddress: '',
    status: 'offline',
    description: ''
  });

  const [locations, setLocations] = useState([]);
  const [devices, setDevices] = useState([]);
  const [deviceTypes, setDeviceTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [loadingData, setLoadingData] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  // Removed locationTreeOpen - now using inline LocationAutocomplete
  const [deviceTreeOpen, setDeviceTreeOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedParentDevice, setSelectedParentDevice] = useState(null);

  // Update selected location when form locationId changes
  useEffect(() => {
    if (formData.locationId && locations.length > 0) {
      const location = locations.find((loc) =>
        (loc.id || loc._id || loc.location_UUID) === formData.locationId
      );
      setSelectedLocation(location || null);
    } else {
      setSelectedLocation(null);
    }
  }, [formData.locationId, locations]);

  // Update selected parent device when form parentDeviceId changes
  useEffect(() => {
    if (formData.parentDeviceId && devices.length > 0) {
      const device = devices.find((dev) =>
        (dev.id || dev._id || dev.device_UUID) === formData.parentDeviceId
      );
      setSelectedParentDevice(device || null);
    } else {
      setSelectedParentDevice(null);
    }
  }, [formData.parentDeviceId, devices]);

  useEffect(() => {
    if (open && !dataLoaded) {
      loadLocationsAndDevices();
    }

    if (open) {
      if (mode === 'edit' && device) {
        // Populate form with existing device data
        // Handle different field names for location ID from API
        const locationId = device.locationId ||
          device.location ||
          device.location_id ||
          device.locationModel?.id ||
          device.locationModel?._id ||
          '';

        setFormData({
          friendlyName: device.friendlyName || '',
          deviceTypeId: device.deviceTypeModel?.id || device.deviceTypeId || '',
          locationId: locationId,
          parentDeviceId: device.parentDeviceId || device.parentDevice || '',
          lanIP: device.config?.identifier?.lanIP || '',
          wanIP: device.config?.identifier?.wanIP || '',
          macAddress: device.config?.identifier?.mac || '',
          status: device.config?.status || 'offline',
          description: device.description || ''
        });
      } else if (mode === 'create') {
        // Set parent device if provided
        setFormData((prev) => ({
          ...prev,
          parentDeviceId: parentDevice?.id || parentDevice?._id || ''
        }));
      }
    }
  }, [open, mode, device, parentDevice]);

  const loadLocationsAndDevices = useCallback(async () => {
    try {
      setLoadingData(true);
      console.log('[DeviceDialog] Loading data...');
      const startTime = performance.now();

      // Load all data in parallel for better performance
      const [locResponse, devResponse, typesResponse] = await Promise.allSettled([
        locationService.getAllLocations({ filters: { limit: 5000 } }),
        devicesApi.getAll({ limit: 1000 }),
        devicesApi.getDeviceTypes({ limit: 100 })
      ]);

      // Extract locations from response structure
      const locData = locResponse.status === 'fulfilled'
        ? (locResponse.value?.data?.data || locResponse.value?.data?.docs || locResponse.value?.data || [])
        : [];
      console.log('[DeviceDialog] Loaded locations:', locData.length);
      setLocations(Array.isArray(locData) ? locData : []);

      // Extract devices
      const devData = devResponse.status === 'fulfilled'
        ? (devResponse.value?.data?.data || devResponse.value?.data || [])
        : [];
      console.log('[DeviceDialog] Loaded devices:', devData.length);
      setDevices(Array.isArray(devData) ? devData : []);

      // Extract device types with fallback to hardcoded types if API fails
      let typesData = [];
      if (typesResponse.status === 'fulfilled') {
        typesData = typesResponse.value?.data?.data || typesResponse.value?.data || [];
      } else {
        console.warn('[DeviceDialog] Device types API failed, using fallback types');
        // Fallback to basic device types if API fails
        typesData = [
          { id: 1, friendlyName: 'Router', type: 'router' },
          { id: 2, friendlyName: 'Switch', type: 'switch' },
          { id: 3, friendlyName: 'Access Point', type: 'wifi' },
          { id: 4, friendlyName: 'Antenna', type: 'antenna' },
          { id: 5, friendlyName: 'Sensor', type: 'sensor' },
          { id: 6, friendlyName: 'Camera', type: 'camera' },
          { id: 7, friendlyName: 'Server', type: 'server' },
          { id: 8, friendlyName: 'Computer', type: 'pc' },
          { id: 9, friendlyName: 'Tablet', type: 'tablet' },
          { id: 10, friendlyName: 'Button', type: 'button' },
          { id: 11, friendlyName: 'Agent', type: 'agent' }
        ];
      }
      console.log('[DeviceDialog] Loaded device types:', typesData.length);
      setDeviceTypes(Array.isArray(typesData) ? typesData : []);

      setDataLoaded(true);
      const loadTime = performance.now() - startTime;
      console.log(`[DeviceDialog] Data loaded in ${loadTime.toFixed(0)}ms`);
    } catch (err) {
      console.error('[DeviceDialog] Error loading data:', err);
      enqueueSnackbar('Failed to load data from API', { variant: 'error' });

      // Use empty arrays on error
      setLocations([]);
      setDevices([]);
      setDeviceTypes([]);
    } finally {
      setLoadingData(false);
    }
  }, [enqueueSnackbar]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData({
      ...formData,
      [field]: value
    });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Prepare device data - ensure we have the correct location ID
      // For edit mode, preserve existing config data
      const existingConfig = mode === 'edit' && device?.config ? device.config : {};

      const deviceData = {
        friendlyName: formData.friendlyName,
        deviceType: formData.deviceTypeId, // API expects 'deviceType' not 'deviceTypeId'
        deviceLocation: formData.locationId || null, // API expects 'deviceLocation' not 'locationId'
        parentDevice: formData.parentDeviceId || null, // API expects 'parentDevice'
        description: formData.description,
        config: {
          ...existingConfig, // Preserve existing config data
          status: formData.status,
          identifier: {
            ...existingConfig.identifier, // Preserve other identifier fields
            lanIP: formData.lanIP || null,
            wanIP: formData.wanIP || null,
            mac: formData.macAddress || null
          }
        }
      };


      if (mode === 'edit' && device) {
        const deviceId = device.id || device._id || device.device_UUID;
        const response = await devicesApi.update(deviceId, deviceData);

        enqueueSnackbar('Device updated successfully!', {
          variant: 'success',
          autoHideDuration: 3000
        });
      } else {
        const response = await devicesApi.create(deviceData);

        enqueueSnackbar('Device created successfully!', {
          variant: 'success',
          autoHideDuration: 3000
        });
      }

      onSave();
      handleClose();
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Failed to save device';
      setError(errorMessage);
      console.error('Error saving device:', err);
      console.error('Error response:', err.response?.data);

      enqueueSnackbar(errorMessage, {
        variant: 'error',
        autoHideDuration: 5000
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = useCallback(() => {
    setFormData({
      friendlyName: '',
      deviceTypeId: '',
      locationId: '',
      parentDeviceId: '',
      lanIP: '',
      wanIP: '',
      macAddress: '',
      status: 'offline',
      description: ''
    });
    setError(null);
    // Don't reset dataLoaded - keep the cached data for next open
    onClose();
  }, [onClose]);

  const isFormValid = () => {
    return formData.friendlyName && formData.deviceTypeId;
  };

  const handleAutoGenerateName = () => {
    // Get selected location (handle different ID field names)
    const selectedLocation = locations.find((l) =>
      l.id === formData.locationId ||
      l._id === formData.locationId ||
      l.location_UUID === formData.locationId
    );
    // Get selected device type
    const selectedType = deviceTypes.find((t) => t.id === formData.deviceTypeId);

    if (!selectedLocation || !selectedType) {
      setError('Please select both location and device type first');
      setTimeout(() => setError(null), 3000);
      return;
    }

    // Helper function to extract site code from location name
    const extractSiteCode = (locationName) => {
      // Check if name starts with 2 letters + 2 numbers pattern
      const siteCodeMatch = locationName.match(/^([A-Z]{2}\d{2})/);
      if (siteCodeMatch) {
        return siteCodeMatch[1]; // e.g., "SH20"
      }
      return null;
    };

    // Helper function to find the site code from the location hierarchy
    const findSiteCode = (location) => {
      if (!location) return null;

      // Check if this location has a site code
      const locationName = location.friendlyName || location.name || '';
      const code = extractSiteCode(locationName);
      if (code) return code;

      // If not a site, try to extract from location name pattern (e.g., "SH20-B01")
      const codeInName = locationName.match(/^([A-Z]{2}\d{2})-/);
      if (codeInName) {
        return codeInName[1];
      }

      // If still no code and has parent, check parent
      if (location.parentLocation || location.parent) {
        const parent = locations.find((loc) =>
          (loc.id || loc._id || loc.location_UUID) === (location.parentLocation || location.parent)
        );
        return findSiteCode(parent);
      }

      return null;
    };

    // Get the site code from the selected location or its hierarchy
    const siteCode = findSiteCode(selectedLocation);
    const locationName = selectedLocation.friendlyName || selectedLocation.name || 'LOC';

    let locationCode = '';

    if (siteCode) {
      // Use site code as the base
      locationCode = siteCode;

      // If the location is not a site itself, add location identifier
      if (!locationName.match(/^[A-Z]{2}\d{2}$/)) {
        // Extract the location-specific part (e.g., from "SH20-B01" get "B01")
        const locPart = locationName.replace(new RegExp(`^${siteCode}-`), '');
        if (locPart && locPart !== locationName) {
          locationCode = `${siteCode}-${locPart}`;
        }
      }
    } else {
      // Fallback to smart abbreviation if no site code found
      const words = locationName.split(/[\s-_]+/);
      if (words.length === 1) {
        locationCode = locationName.substring(0, Math.min(4, locationName.length)).toUpperCase();
      } else {
        locationCode = words.map((w) => w.charAt(0)).join('').toUpperCase();
        if (locationCode.length > 4) {
          locationCode = locationCode.substring(0, 4);
        }
      }
    }

    // Get device type abbreviation from friendlyName or type field
    const deviceTypeAbbr = {
      'router': 'RTR',
      'switch': 'SW',
      'wifi': 'AP',
      'access point': 'AP',
      'antenna': 'ANT',
      'sensor': 'SNS',
      'camera': 'CAM',
      'server': 'SRV',
      'pc': 'PC',
      'computer': 'PC',
      'tablet': 'TAB',
      'button': 'BTN',
      'agent': 'AGT'
    }[(selectedType.type || selectedType.friendlyName || '').toLowerCase()] ||
      (selectedType.friendlyName || selectedType.name || 'DEV').substring(0, 3).toUpperCase();

    // Count existing devices with similar names (for numbering)
    const basePattern = `${locationCode}-${deviceTypeAbbr}`;
    const similarDevices = devices.filter((d) =>
      d.friendlyName && d.friendlyName.startsWith(basePattern)
    );
    const deviceNumber = similarDevices.length + 1;

    // Generate friendly name
    // Examples: SH20-RTR-01 (router at site SH20)
    //          SH20-B01-SW-01 (switch in building B01 at site SH20)
    //          SH20-R15-AP-01 (access point in room R15 at site SH20)
    const generatedName = `${locationCode}-${deviceTypeAbbr}-${String(deviceNumber).padStart(2, '0')}`;

    setFormData({
      ...formData,
      friendlyName: generatedName
    });

    // Clear any error
    setError(null);
  };

  // Removed handleLocationSelect - now handled inline by LocationAutocomplete

  const handleParentDeviceSelect = (device) => {
    setSelectedParentDevice(device);
    const devId = device ? device.id || device._id || device.device_UUID : '';
    setFormData({ ...formData, parentDeviceId: devId });
    setDeviceTreeOpen(false);
  };

  return (
    <>
      <ResponsiveDialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth>

        <DialogTitle>
          {t?.(mode === 'edit' ? "DeviceManagement.update_device" : "DeviceManagement.create_device") || (mode === 'edit' ? "Update Device" : "Create Device")}
        </DialogTitle>
        <DialogContent>
          {loadingData ?
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {/* Location Tree Skeleton */}
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  <Skeleton width="30%" />
                </Typography>
                <Skeleton variant="rectangular" height={56} sx={{ mb: 1, borderRadius: 1 }} />
                <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
                  <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 2 }} />
                  <Skeleton variant="rectangular" width={100} height={32} sx={{ borderRadius: 2 }} />
                </Box>
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
              </Grid>

              {/* Device Type Skeleton */}
              <Grid item xs={12} md={6}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>

              {/* Device Name Skeleton */}
              <Grid item xs={12}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>

              {/* Parent Device Skeleton */}
              <Grid item xs={12} md={6}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>

              {/* IP Addresses Skeleton */}
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>
              <Grid item xs={12} md={4}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>

              {/* Status Skeleton */}
              <Grid item xs={12} md={6}>
                <Skeleton variant="rectangular" height={56} sx={{ borderRadius: 1 }} />
              </Grid>

              {/* Description Skeleton */}
              <Grid item xs={12}>
                <Skeleton variant="rectangular" height={96} sx={{ borderRadius: 1 }} />
              </Grid>
            </Grid> :

            <Grid container spacing={2} sx={{ mt: 1 }}>
              {error &&
                <Grid item xs={12}>
                  <Alert severity="error" onClose={() => setError(null)}>
                    {error}
                  </Alert>
                </Grid>
              }

              <Grid item xs={12}>
                <LocationTreeView
                  value={selectedLocation}
                  onChange={(event, newValue) => {
                    console.log('[DeviceDialog] Location onChange called:', { event, newValue });
                    setSelectedLocation(newValue);
                    const locId = newValue ?
                      (newValue.id || newValue._id || newValue.location_UUID) : '';
                    console.log('[DeviceDialog] Setting locationId to:', locId);
                    setFormData(prev => {
                      const updated = { ...prev, locationId: locId };
                      console.log('[DeviceDialog] Updated formData:', updated);
                      return updated;
                    });
                  }}
                  locations={locations}
                  loading={loadingData}
                  disabled={loading || loadingData}
                  required
                  label={t ? t("DeviceManagement.location") : "Location"}
                  helperText={selectedLocation ?
                    `Selected: ${selectedLocation.friendlyName || selectedLocation.name}` :
                    (t ? t("DeviceManagement.location_helper") : "Select a location from the tree below")}
                  showFavorites={true}
                  autoExpandFavorites={true}
                  autoExpandSelected={true}
                  maxHeight={400}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth required disabled={loadingData || deviceTypes.length === 0}>
                  <InputLabel>{t ? t("DeviceManagement.device_type") : "Device Type"}</InputLabel>
                  <Select
                    value={formData.deviceTypeId}
                    onChange={handleChange('deviceTypeId')}
                    label={t ? t("DeviceManagement.device_type") : "Device Type"}>

                    {deviceTypes.map((type) =>
                      <MenuItem key={type.id || type._id} value={type.id || type._id}>
                        {type.friendlyName || type.name || type.type || 'Unknown Type'}
                      </MenuItem>
                    )}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t ? t("DeviceManagement.device_name") : "Device Name"}
                  value={formData.friendlyName}
                  onChange={handleChange('friendlyName')}
                  required
                  helperText={t ? t("DeviceManagement.enter_friendly_name_click_autogener") : "Enter a friendly name or click auto-generate"}
                  InputProps={{
                    endAdornment:
                      <InputAdornment position="end">
                        <Tooltip title={t ? t("DeviceManagement.autogenerate_name_from_location_devi") : "Auto-generate name from location and device type"}>
                          <span>
                            <IconButton
                              onClick={handleAutoGenerateName}
                              edge="end"
                              disabled={!formData.locationId || !formData.deviceTypeId}>

                              <AutoGenerateIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </InputAdornment>

                  }} />

              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label={t ? t("DeviceManagement.parent_device_optional") : "Parent Device (Optional)"}
                  value={selectedParentDevice ? `${selectedParentDevice.friendlyName || 'Device ' + selectedParentDevice.id}` : ''}
                  onClick={() => setDeviceTreeOpen(true)}
                  disabled={loading || loadingData}
                  InputProps={{
                    readOnly: true,
                    endAdornment: selectedParentDevice &&
                      <InputAdornment position="end">
                        <Chip
                          size="small"
                          icon={<Devices />}
                          label={selectedParentDevice.deviceTypeModel?.friendlyName || 'Device'}
                          onDelete={() => {
                            setSelectedParentDevice(null);
                            setFormData({ ...formData, parentDeviceId: '' });
                          }} />

                      </InputAdornment>,

                    style: { cursor: 'pointer' }
                  }}
                  placeholder={t ? t("DeviceManagement.click_select_parent_device") : "Click to select parent device"} />

              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t ? t("DeviceManagement.lan_ip_address") : "LAN IP Address"}
                  value={formData.lanIP}
                  onChange={handleChange('lanIP')}
                  placeholder="192.168.1.1" />

              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t ? t("DeviceManagement.wan_ip_address") : "WAN IP Address"}
                  value={formData.wanIP}
                  onChange={handleChange('wanIP')}
                  placeholder={t ? t("DeviceManagement.public_ip") : "Public IP"} />

              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label={t ? t("DeviceManagement.mac_address") : "MAC Address"}
                  value={formData.macAddress}
                  onChange={handleChange('macAddress')}
                  placeholder="00:00:00:00:00:00" />

              </Grid>

              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>{t ? t("DeviceManagement.status") : "Status"}</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={handleChange('status')}
                    label={t ? t("DeviceManagement.status") : "Status"}>

                    <MenuItem value="online">{t ? t("DeviceManagement.online") : "Online"}</MenuItem>
                    <MenuItem value="offline">{t ? t("DeviceManagement.offline") : "Offline"}</MenuItem>
                    <MenuItem value="maintenance">{t ? t("DeviceManagement.maintenance") : "Maintenance"}</MenuItem>
                    <MenuItem value="error">{t ? t("DeviceManagement.error") : "Error"}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label={t ? t("DeviceManagement.description") : "Description"}
                  value={formData.description}
                  onChange={handleChange('description')}
                  placeholder={t ? t("DeviceManagement.enter_device_description") : "Enter device description..."} />

              </Grid>
            </Grid>
          }
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>{t ? t("DeviceManagement.cancel") : "Cancel"}

          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading || !isFormValid()}>

            {loading ? (
              <CircularProgress size={24} />
            ) : (
              t?.(mode === 'edit' ? "DeviceManagement.update_device" : "DeviceManagement.create_device") ||
              (mode === 'edit' ? "Update" : "Create")
            )}
          </Button>
        </DialogActions>
      </ResponsiveDialog>

      {/* Location TreeSelect removed - now using inline LocationAutocomplete */}


      <TreeSelect
        open={deviceTreeOpen}
        onClose={() => setDeviceTreeOpen(false)}
        onSelect={handleParentDeviceSelect}
        data={devices.filter((d) => (d.id || d._id || d.device_UUID) !== (device?.id || device?._id || device?.device_UUID))}
        selectedId={formData.parentDeviceId}
        title={t ? t("DeviceManagement.select_parent_device") : "Select Parent Device"}
        type="device"
        loading={loadingData}
        allowNone={true} />

    </>);

};

export default DeviceDialog;